import { cn } from "../ui/cn";

// ---------------------------------------------------------------------------
// Avatar de iniciales de una persona del padrón (CC-CONSOLE#1595).
//
// Nunca foto: el personal de ejemplo y el real se muestran igual, con sus
// iniciales sobre un color. El color sale de la ficha (`avatarColor`) o, si no
// lo trae, de un hash estable del nombre, así cada persona conserva su color
// en el directorio, el tablero y la nómina.
// ---------------------------------------------------------------------------

/** Paleta para personas sin color propio: tonos medios legibles en claro y oscuro. */
const FALLBACK_COLORS = ["#7C3AED", "#2563EB", "#0D9488", "#DB2777", "#EA580C", "#4F46E5", "#16A34A", "#C026D3", "#0284C7", "#D97706", "#E11D48", "#059669"];

export const AVATAR_SIZES = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
} as const satisfies Record<string, string>;

export type AvatarSize = keyof typeof AVATAR_SIZES;

/** Iniciales (máximo dos) de un nombre; «?» si no hay nombre. */
export function personInitials(name: string | null | undefined): string {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Color estable de una persona: el de su ficha o uno derivado del nombre. */
export function personColor(name: string | null | undefined, color?: string | null): string {
  if (color && /^#[0-9a-f]{6}$/i.test(color)) return color;
  let h = 0;
  for (const ch of String(name ?? "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length];
}

export const PRESENCE_DOT = {
  online: "bg-success",
  away: "bg-warning",
  offline: "bg-content-muted",
  alert: "bg-error",
} as const satisfies Record<string, string>;

export type Presence = keyof typeof PRESENCE_DOT;

interface Props {
  name: string | null | undefined;
  color?: string | null;
  size?: AvatarSize;
  /** Puntito de estado en la esquina (en línea / ausente / baja / alerta). */
  presence?: Presence | null;
  className?: string;
}

export default function PersonAvatar({ name, color, size = "sm", presence = null, className }: Props) {
  const bg = personColor(name, color);
  return (
    <span className={cn("relative inline-flex shrink-0", className)} title={name ?? undefined} data-testid="person-avatar">
      <span
        aria-hidden="true"
        className={cn("flex items-center justify-center rounded-full font-semibold text-white ring-2 ring-card", AVATAR_SIZES[size])}
        style={{ backgroundColor: bg }}
      >
        {personInitials(name)}
      </span>
      {presence && (
        <span
          className={cn("absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card", PRESENCE_DOT[presence])}
          data-testid={`presence-${presence}`}
        />
      )}
    </span>
  );
}
